import type { FirebaseApp } from 'firebase/app';
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
  type Functions,
} from 'firebase/functions';
import { doc, getDoc, type Firestore } from 'firebase/firestore';
import type { EmulatorEndpoint } from '@taki/runtime-config';

/**
 * LE RELAIS DE DONNEES DE MARCHE, COTE APP.
 *
 * Une app ne parle jamais a un fournisseur de cours : sa clef d'API partirait
 * dans le bundle navigateur, ou n'importe qui la lit dans l'onglet reseau. Elle
 * parle a ce client, qui appelle une Cloud Function, qui garde la clef.
 *
 * Ce que l'app NE SAIT PAS, et ne doit jamais apprendre : le nom du
 * fournisseur, son adresse, sa forme de reponse, sa clef. Elle connait les
 * types de ce fichier, c'est tout.
 *
 * LES TYPES CI-DESSOUS SONT UNE RECOPIE de `functions/src/market/contract.ts`.
 * C'est assume et c'est encadre : `functions/` ne peut pas dependre d'un package
 * `@taki/*` (le deploiement Firebase installe ses dependances avec npm, dans le
 * cloud, ou aucun `workspace:*` ne se resout : docs/FUNCTIONS.md). Deux
 * garde-fous rendent la derive impossible en silence :
 *   - `MARKET_CONTRACT_VERSION` est compare a chaque reponse, a l'execution ;
 *   - `tests/functions-region.test.mjs` lit LES DEUX fichiers et echoue si la
 *     version ou la region se separent.
 */

/* -------------------------------------------------------------------------- */
/* Le contrat (recopie de functions/src/market/contract.ts)                    */
/* -------------------------------------------------------------------------- */

/** Version de la FORME des reponses attendue par ce client. */
export const MARKET_CONTRACT_VERSION = 1;

/**
 * Region des Cloud Functions. DOIT valoir celle de `functions/src/runtime.ts` :
 * viser la mauvaise region fait echouer l'appel sur un "Failed to fetch" qui ne
 * dit rien. Le test cite plus haut compare les deux fichiers.
 */
export const MARKET_FUNCTIONS_REGION = 'europe-west1';

/** Fournisseur derriere le relais. */
export type MarketProvider = 'eodhd';

/**
 * Etat d'une reponse.
 *   - `ok`         : le fournisseur a repondu avec de la donnee ;
 *   - `not-found`  : il ne connait pas cet instrument. UN FAIT, PAS UNE PANNE :
 *                    un titre radie n'a plus de cours nulle part, et l'ecran
 *                    doit l'afficher "non cote" au lieu de clignoter en rouge ;
 *   - `no-data`    : il le connait, mais n'a rien sur la periode demandee.
 */
export type MarketStatus = 'ok' | 'not-found' | 'no-data';

/** Un instrument cotable, tel que le fournisseur le nomme. */
export interface MarketInstrument {
  /** L'identifiant a redonner a `quote` et `splits` (ex. "GOOGL.US"). */
  symbol: string;
  code: string;
  exchange: string;
  name: string;
  kind: string | null;
  country: string | null;
  currency: string | null;
  isin: string | null;
  /** L'ISIN de cette ligne est EXACTEMENT celui demande (recherche approchante). */
  isinMatch: boolean;
}

/** Un cours de cloture, pour une journee de cotation. */
export interface MarketPricePoint {
  /** Jour de cotation `AAAA-MM-JJ`. Date PURE, jamais un instant. */
  date: string;
  close: number;
  currency: string | null;
  /** `real-time` = dernier cours connu (provisoire) ; `eod` = cloture officielle. */
  source: 'real-time' | 'eod';
  /** Instant date par le fournisseur, ISO 8601, quand il en donne un. */
  at?: string;
}

/** Un split (division ou regroupement d'actions). */
export interface MarketSplit {
  /** Jour d'effet `AAAA-MM-JJ`. */
  date: string;
  /** Actions NEUVES recues (le 20 de "20 pour 1"). */
  numerator: number;
  /** Actions ANCIENNES rendues (le 1 de "20 pour 1"). */
  denominator: number;
  /** Facteur a appliquer a une quantite detenue : 20 pour un 20:1. */
  ratio: number;
  /** La chaine brute du fournisseur, pour verification a la main. */
  raw: string;
}

interface MarketResponseBase {
  provider: MarketProvider;
  status: MarketStatus;
  fetchedAt: string;
  contractVersion: number;
}

export interface MarketSearchResponse extends MarketResponseBase {
  op: 'search';
  isin: string;
  /** Correspondances exactes d'abord. Vide quand `status` vaut `not-found`. */
  instruments: MarketInstrument[];
}

export interface MarketQuoteResponse extends MarketResponseBase {
  op: 'quote';
  symbol: string;
  latest: MarketPricePoint | null;
  /** Rempli seulement quand `from` a ete fourni. */
  history: MarketPricePoint[];
  /** `latest` vient du cache (aucun appel au fournisseur). Un fait, pas une alerte. */
  fromCache: boolean;
}

export interface MarketSplitsResponse extends MarketResponseBase {
  op: 'splits';
  symbol: string;
  /** Vide avec `status: 'ok'` = cet instrument n'a jamais splitte. */
  splits: MarketSplit[];
}

/** Etat publie de la clef. Il ne porte JAMAIS la valeur. */
export interface MarketKeyState {
  provider: MarketProvider;
  /** Instant de la derniere pose, ISO 8601. */
  keySetAt: string;
  /** Les quatre derniers caracteres, pour reconnaitre la clef sans la lire. */
  keyLast4: string;
  /** uid de la personne qui l'a posee. */
  keySetBy: string;
}

/* -------------------------------------------------------------------------- */
/* Erreurs                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Ce que l'app doit savoir distinguer, et qui n'est PAS la meme conversation
 * avec l'utilisateur :
 *   - `missing-key`            : personne n'a encore configure la clef. L'ecran
 *                                envoie vers les reglages, il n'affiche pas une
 *                                panne ;
 *   - `provider-rejected-key`  : la clef existe mais le fournisseur la refuse
 *                                (revoquee, expiree, mal collee) ;
 *   - `insufficient-role`      : ce compte n'a pas le droit de changer la clef ;
 *   - `provider-quota`         : quota d'appels atteint. Ca repartira tout seul ;
 *   - `provider-unreachable`   : le fournisseur ne repond pas. Reessayer ;
 *   - `contract-mismatch`      : le relais deploye et cette app ne parlent pas
 *                                la meme langue. Un deploiement a ete oublie ;
 *   - `unknown`                : tout le reste.
 */
export type MarketErrorReason =
  | 'missing-key'
  | 'provider-rejected-key'
  | 'insufficient-role'
  | 'provider-quota'
  | 'provider-unreachable'
  | 'provider-error'
  | 'contract-mismatch'
  | 'unknown';

/** Erreur du relais, avec une raison exploitable par un ecran. */
export class MarketDataError extends Error {
  override readonly name = 'MarketDataError';
  readonly reason: MarketErrorReason;

  constructor(message: string, reason: MarketErrorReason, options?: { cause?: unknown }) {
    super(message);
    this.reason = reason;
    if (options?.cause !== undefined) this.cause = options.cause;
    Object.setPrototypeOf(this, MarketDataError.prototype);
  }
}

/** Extrait la raison portee par les `details` d'une HttpsError, sinon devine. */
function reasonOf(error: unknown): MarketErrorReason {
  const details = (error as { details?: unknown } | null)?.details;
  const raw =
    typeof details === 'object' && details !== null
      ? (details as Record<string, unknown>)['reason']
      : undefined;

  const known: readonly MarketErrorReason[] = [
    'missing-key',
    'provider-rejected-key',
    'insufficient-role',
    'provider-quota',
    'provider-unreachable',
    'provider-error',
  ];
  return known.find((candidate) => candidate === raw) ?? 'unknown';
}

/* -------------------------------------------------------------------------- */
/* Client                                                                      */
/* -------------------------------------------------------------------------- */

/** Le relais, tel qu'une app le voit. */
export interface MarketDataClient {
  /** Un ISIN vers les cotations possibles (correspondances exactes d'abord). */
  search(isin: string): Promise<MarketSearchResponse>;
  /**
   * Le cours d'un symbole. Sans `from`, le dernier cours connu (servi par le
   * cache s'il est frais). Avec `from`, la serie des clotures depuis cette date.
   */
  quote(symbol: string, from?: string): Promise<MarketQuoteResponse>;
  /** L'historique des splits, pour corriger une quantite detenue. */
  splits(symbol: string, from?: string): Promise<MarketSplitsResponse>;
  /**
   * L'etat publie de la clef, ou null si aucune n'est posee. LECTURE DIRECTE du
   * document d'etat (pas d'appel de Function) : c'est ce que les rules
   * autorisent, et un ecran de reglages ne doit pas payer un demarrage a froid
   * pour afficher une date.
   */
  keyState(): Promise<MarketKeyState | null>;
  /**
   * Pose la clef. Reserve a owner/admin. La clef est VERIFIEE chez le
   * fournisseur avant d'etre rangee : une faute de frappe leve ici, tout de
   * suite, et rien n'est ecrit.
   */
  setKey(apiKey: string): Promise<MarketKeyState>;
  /** Retire la clef et son etat publie. Reserve a owner/admin. */
  clearKey(): Promise<void>;
}

/** Instances Functions deja branchees sur l'emulateur (cf. `initFirebase`). */
const emulatorBoundFunctions = new WeakSet<Functions>();

/**
 * Construit le client du relais.
 *
 * Branchement emulateur : pilote par la CONFIG RUNTIME (`emulators.functions`),
 * jamais par une variable figee au build, comme pour Firestore et Auth.
 */
export function createMarketDataClient(
  app: FirebaseApp,
  db: Firestore,
  orgId: string,
  emulator?: EmulatorEndpoint,
): MarketDataClient {
  const functions = getFunctions(app, MARKET_FUNCTIONS_REGION);

  if (emulator && !emulatorBoundFunctions.has(functions)) {
    connectFunctionsEmulator(functions, emulator.host, emulator.port);
    emulatorBoundFunctions.add(functions);
    console.info(
      `[@taki/sdk] Cloud Functions branchees sur l'emulateur ${emulator.host}:${emulator.port}.`,
    );
  }

  const callMarketData = httpsCallable<unknown, unknown>(functions, 'marketData');
  const callMarketKey = httpsCallable<unknown, unknown>(functions, 'marketKey');

  /**
   * Appelle une callable et traduit ses echecs.
   *
   * Le `try/catch` ne sert pas a masquer : il sert a ce qu'un ecran puisse
   * repondre "va configurer la clef" plutot que d'afficher un code Firebase.
   */
  async function call<T>(
    callable: typeof callMarketData,
    payload: Record<string, unknown>,
  ): Promise<T> {
    try {
      const result = await callable(payload);
      return result.data as T;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new MarketDataError(message, reasonOf(error), { cause: error });
    }
  }

  /**
   * Verifie que le relais deploye parle bien la meme langue que ce client.
   *
   * Sans ce controle, un socle deploye en retard rendrait des champs absents ou
   * de sens different, et l'ecran afficherait un patrimoine faux sans que rien
   * ne signale quoi que ce soit. On prefere une erreur nette.
   */
  function checkContract<T extends { contractVersion?: number }>(response: T): T {
    if (response?.contractVersion !== MARKET_CONTRACT_VERSION) {
      throw new MarketDataError(
        `Le relais de donnees de marche repond en version ${String(response?.contractVersion)}, ` +
          `cette app attend la version ${MARKET_CONTRACT_VERSION}. Le socle et les Functions ne sont ` +
          `pas deployes ensemble.`,
        'contract-mismatch',
      );
    }
    return response;
  }

  return {
    async search(isin) {
      return checkContract(
        await call<MarketSearchResponse>(callMarketData, { op: 'search', isin }),
      );
    },

    async quote(symbol, from) {
      return checkContract(
        await call<MarketQuoteResponse>(callMarketData, {
          op: 'quote',
          symbol,
          ...(from != null ? { from } : {}),
        }),
      );
    },

    async splits(symbol, from) {
      return checkContract(
        await call<MarketSplitsResponse>(callMarketData, {
          op: 'splits',
          symbol,
          ...(from != null ? { from } : {}),
        }),
      );
    },

    async keyState() {
      const snapshot = await getDoc(doc(db, 'orgs', orgId, 'secretStates', 'market'));
      if (!snapshot.exists()) return null;
      const data = snapshot.data();
      const keySetAt = data['keySetAt'];
      return {
        provider: 'eodhd',
        // Le document porte un Timestamp Firestore ; l'etat expose une chaine
        // ISO, comme tout ce que rend le relais.
        keySetAt:
          typeof keySetAt === 'object' && keySetAt !== null && 'toDate' in keySetAt
            ? (keySetAt as { toDate: () => Date }).toDate().toISOString()
            : String(keySetAt ?? ''),
        keyLast4: typeof data['keyLast4'] === 'string' ? data['keyLast4'] : '',
        keySetBy: typeof data['keySetBy'] === 'string' ? data['keySetBy'] : '',
      };
    },

    async setKey(apiKey) {
      const result = await call<{ state: MarketKeyState | null }>(callMarketKey, {
        op: 'set',
        apiKey,
      });
      if (result.state == null) {
        throw new MarketDataError(`Le relais n'a pas confirme la pose de la clef.`, 'unknown');
      }
      return result.state;
    },

    async clearKey() {
      await call<{ state: null }>(callMarketKey, { op: 'clear' });
    },
  };
}
